import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { GRADES as REAL_GRADES } from '../data';

// Season stays a fixed list — crushing seasons aren't a field on any mill record to derive from the
// way State/District/Grade are; they're a business calendar concept, not mill data.
const SEASONS = ['All Seasons', '2025-26', '2024-25', '2023-24', '2022-23'];
const GRADES = ['All Grades', ...REAL_GRADES];

const FilterBar = ({ filters, onChange, factories }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // State/District options derived from the real fetched mill data (same factories list index.jsx
  // gets from data.js's fetchFactoryCards) instead of a static 2-state lookup table that only ever
  // covered Maharashtra/Karnataka against a real universe spanning 15 states.
  const states = ['All States', ...[...new Set(factories?.map(f => f?.state)?.filter(Boolean))]?.sort()];
  const districts = filters?.state === 'All States'
    ? ['All Districts']
    : ['All Districts', ...[...new Set(
        factories?.filter(f => f?.state === filters?.state)?.map(f => f?.district)?.filter(Boolean)
      )]?.sort()];

  const handleState = (e) => {
    onChange({ ...filters, state: e?.target?.value, district: 'All Districts' });
  };

  const FilterContent = () => (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      {/* State */}
      <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>State</label>
        <select
          value={filters?.state}
          onChange={handleState}
          className="rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
          style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
        >
          {states?.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
      {/* District */}
      <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>District</label>
        <select
          value={filters?.district}
          onChange={e => onChange({ ...filters, district: e?.target?.value })}
          className="rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
          style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
        >
          {districts?.map(d => <option key={d}>{d}</option>)}
        </select>
      </div>
      {/* Season */}
      <div className="flex flex-col gap-1 flex-1 min-w-[130px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>Season</label>
        <select
          value={filters?.season}
          onChange={e => onChange({ ...filters, season: e?.target?.value })}
          className="rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
          style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
        >
          {SEASONS?.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
      {/* Grade */}
      <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>Grade</label>
        <select
          value={filters?.grade}
          onChange={e => onChange({ ...filters, grade: e?.target?.value })}
          className="rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
          style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
        >
          {GRADES?.map(g => <option key={g}>{g}</option>)}
        </select>
      </div>
      {/* Active Quota Toggle */}
      <div className="flex flex-col gap-1 justify-end">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>Quota Filter</label>
        <button
          onClick={() => onChange({ ...filters, activeQuotaOnly: !filters?.activeQuotaOnly })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all"
          style={{
            borderColor: filters?.activeQuotaOnly ? '#f59e0b' : '#e8ddd0',
            background: filters?.activeQuotaOnly ? '#fef3c7' : '#fff',
            color: filters?.activeQuotaOnly ? '#92400e' : '#6b7280',
          }}
        >
          <div
            className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
            style={{
              borderColor: filters?.activeQuotaOnly ? '#f59e0b' : '#d1d5db',
              background: filters?.activeQuotaOnly ? '#f59e0b' : 'transparent',
            }}
          >
            {filters?.activeQuotaOnly && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
          Active Quota Only
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop filter bar */}
      <div className="hidden sm:block bg-white rounded-2xl border p-4" style={{ borderColor: '#e8ddd0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <FilterContent />
      </div>
      {/* Mobile: single "Filters" button + bottom drawer */}
      <div className="sm:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border font-semibold text-sm"
          style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
        >
          <Icon name="SlidersHorizontal" size={16} color="#f59e0b" />
          Filters
          {(filters?.state !== 'All States' || filters?.district !== 'All Districts' || filters?.season !== 'All Seasons' || filters?.grade !== 'All Grades') && (
            <span className="w-2 h-2 rounded-full bg-amber-500" />
          )}
        </button>

        {/* Bottom drawer overlay */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
            <div className="relative bg-white rounded-t-3xl p-6 pb-8 z-10" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold" style={{ color: '#1f2937' }}>Filters</h3>
                <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-lg hover:bg-gray-100">
                  <Icon name="X" size={18} color="#6b7280" />
                </button>
              </div>
              <FilterContent />
              <button
                onClick={() => setDrawerOpen(false)}
                className="mt-5 w-full py-3 rounded-xl font-bold text-white text-sm"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FilterBar;
